import BaseAPIClient from './base/BaseAPIClient';

import UserModel from '../models/UserModel';
import ArticleModel from '../models/ArticleModel';
import CollegeModel from '../models/CollegeModel';
import S3PhotoModel from '../models/S3PhotoModel';
import PatientProfileModel from '../models/PatientProfileModel';
import LocationModel from '../models/LocationModel';
import ListResponseModel from '../models/ListResponseModel';
import CounselorProfileModel from '../models/CounselorProfileModel';
import SchoolModel from '../models/SchoolModel';
import SurveyModel from '../models/Survey/SurveyModel';
import CounselingPreferencesModel from '../models/CounselingPreferencesModel.js';

import MessageThreadModel from '../models/MessageThreadModel';
import MessageModel from '../models/MessageModel';

export default class UserClient extends BaseAPIClient {
  async getMagicCode(email) {
    const response = await this._post('v2/platform/users/magic-code/', {
      email
    });
    return {
      token: response.token
    };
  }

  async loginWithMagicCode(token, secret) {
    const response = await this._post('v2/platform/users/magic-code/login/', {
      token,
      secret
    });
    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken
    };
  }
  async getUnreadMessageCount() {
    const response = await this._get('users/me/profile/');
    const user = new UserModel(response);

    return user._unread_messages_count;
  }
  async getRemoteUser() {
    const user = await this._get('users/me/profile/').then(
      (user) => new UserModel(user)
    );

    if (user && user.isStudent) {
      const profile = await this.getPatientProfile(user.id);
      user.student_profile = profile;
      
      await this._post('v2/preference/student/', { studentUserId: user.id })

      const school = await this.getCampusView(user.id);
      user.school = school;

      const num_pending_payments = await this.getNumberOfPendingPayments();
      user.num_pending_payments = num_pending_payments || 0;

      const preferences = await this._get('users/me/counseling_preferences/').then(m => new CounselingPreferencesModel(m));
      user.counseling_preferences = preferences;
    }

    if (user && user.isCounselor) {
      const profile = await this._get('counselors/me/counselor_profile/').then(
        (user) => new CounselorProfileModel(user)
      );
      user.counselor_profile = profile;
    }

    if (user && user.isCollegeAdmin) {
      const school = await this.getMyCollege();
      user.school = school;
    }

    return user;
  }
  getCampusView(userId){
    return this._get('v2/campus-administration/student/campus/', { 
      userId 
    }).then(r => new CollegeModel({
      id: r.id,
      name: r.name, 
      logo: r.logoUrl ? new S3PhotoModel({ signed_url: r.logoUrl }) : null,
      banner_image: r.bannerImageUrl ? new S3PhotoModel({ signed_url: r.bannerImageUrl }) : null,
      messageForNewUserPrompt: r.messageForNewUserPrompt || '',
      counseling_center_location: r.counselingCenter.location || '',
      counseling_center_phone: r.counselingCenter.phone || '',
      main_counselor_name: r.counselor.name || '',
      main_counselor_phone: r.counselor.phone || '',
      campus_crisis_info: r.crisisInfo || '',
      bettermynd_crisis_phone: r.features.crisisPhone || '',
      is_member: r.isMember || false,
    }));

  }
  requestSchool(school_name, name, email, message) {
    return this._post('users/request_school/', {
      school_name,
      name,
      email,
      message
    });
  }

  sendMessage(receiver, heading, message, message_thread) {
    const data = {
      receiver,
      heading,
      message
    };

    if (message_thread) {
      data.message_thread = message_thread;
    }

    return this._post('messages/send/', data);
  }

  // side bar
  getMessageThreads(limit = 1000, offset = 0) {
    return this._get('messages/threads/', {
      limit,
      offset
    }).then((m) => new ListResponseModel(m, MessageThreadModel));
  }

  // messages within a thread
  getMessages(message_thread, limit = 1000, offset = 0) {
    return this._get('messages/', {
      message_thread,
      limit,
      offset
      // todo: ordering: '-timestamp',
    }).then((m) => new ListResponseModel(m, MessageModel));
  }

  markAsRead(threadId) {
    return this._post(`messages/threads/${threadId}/read/`);
  }

  getNumberOfPendingPayments() {
    return this._get('counseling_sessions/pending_payments/').then(
      (res) => res.num_pending_payments || 0
    );
  }

  getCounselorProfile(id) {
    return this._get(`counselors/counselors/${id}/profile/`).then(
      (m) => new CounselorProfileModel(m)
    );
  }

  getCounselorCronofyElementToken(id) {
    return this._get(`counselors/counselors/${id}/cronofy_element_token`);
  }

  getStudentProfile(id) {
    return this.getPatientProfile(id);
    // return this._get(`users/students/${id}/`).then(m => new StudentProfileModel(m));
  }

  getPatientProfile(id) {
    return this._get(`counselors/patients/${id}/profile/`).then(
      (m) => new PatientProfileModel(m)
    );
  }

  getMyCollege() {
    return this._get('schools/my_school/').then((m) => new CollegeModel(m));
  }

  putCurrentUserUpdatePassword(old_password, password) {
    return this._put('users/me/password/', {
      old_password,
      password
    });
  }

  updateUserAccount(
    first_name,
    last_name,
    date_of_birth,
    phone_number,
    allow_sms_notifications,
    gender,
    race,
    city,
    bio
  ) {
    const data = {
      first_name,
      last_name,
      date_of_birth,
      phone_number,
      allow_sms_notifications,
      gender,
      race,
      bio
    };

    if (city) {
      data.city = city;
    }

    return this._patch('users/me/profile/', data);
  }

  updateStudentAccount({
    international_student,
    student_athlete,
    student_status,
    completed_survey,
    live_on_campus,
    student_id_number,
    class_year,
    city_of_residence
  }) {
    return this._patch('users/me/student_profile/', {
      international_student,
      student_athlete,
      student_status,
      completed_survey,
      live_on_campus,
      student_id_number,
      class_year,
      city_of_residence
    });
  }

  registerStudent(first_name, last_name, school_id) {
    const data = {
      firstName: first_name,
      lastName: last_name
    };

    if (school_id)
      data.schoolId = school_id;

    return this._post('v2/campus-administration/student/register/', data);
  }
  autoRegisterAsStudent() {
    return this._post('v2/campus-administration/student/auto-register/', { });
  }

  registerCounselor(email, first_name, last_name) {
    const data = {
      email,
      first_name,
      last_name,
      on_campus_counselor: false
    };

    return this._postUnauthenticated('users/counselor_register/', data);
  }

  getCityList(search, limit = 10, offset = 0) {
    return this._get('locations/cities/', {
      limit,
      offset,
      search
    }).then((m) => new ListResponseModel(m, LocationModel));
  }

  getStatesList(search, limit = 10, offset = 0) {
    return this._get('locations/states/', {
      limit,
      offset,
      search
    }).then((m) => new ListResponseModel(m, LocationModel));
  }

  getSpecialtiesList(search, limit = 10, offset = 0) {
    return this._get('counselors/specialty_areas/', {
      limit,
      offset,
      search
    }).then((m) => new ListResponseModel(m, LocationModel));
  }

  getAllInsurances(search, limit = 10, offset = 0) {
    return this._get('counselors/insurance_carriers/', {
      limit,
      offset,
      search
    }).then((m) => new ListResponseModel(m, LocationModel));
  }

  saveProfilePhoto(photo_id) {
    return this._patch('users/me/profile/', {
      photo: photo_id
    }).then((user) => new UserModel(user));
  }

  saveWelcomeStepOne(
    date_of_birth,
    city,
    gender,
    race,
    phone_number,
    allow_sms_notifications
  ) {
    return this._patch('users/me/profile/', {
      date_of_birth,
      city,
      gender,
      race,
      phone_number,
      allow_sms_notifications
    }).then((user) => new UserModel(user));
  }

  saveWelcomeStepTwo(
    student_status,
    international_student,
    student_athlete,
    class_year
  ) {
    return this._patch('users/me/student_profile/', {
      student_status,
      international_student,
      student_athlete,
      class_year
    });
  }

  saveCounselorProfile(data) {
    return this._patch('counselors/me/counselor_profile/', data);
  }

  forgotPassword(email) {
    return this._post('users/forgot_password/', {
      email
    });
  }

  resetPassword(token, password, recaptcha) {
    return this._post('users/reset_password/', {
      token,
      password,
      recaptcha
    });
  }

  submitContact(email, name, message) {
    return this._post('users/contact_us/', {
      email,
      name,
      message
    });
  }

  getCollegeFromEmail(email) {
    let domain = '';

    const emailParts = (email || '').split('@');

    if (emailParts.length === 2) {
      domain = emailParts[1];
    }

    return this._getUnauthenticated('schools/schools/', {
      email_domains__domain: domain
    }).then((m) => new ListResponseModel(m, SchoolModel));
  }

  getCollegeList(search, limit = 10, offset = 0) {
    return this._get('schools/schools/', {
      limit,
      offset,
      search
    }).then((m) => new ListResponseModel(m, SchoolModel));
  }

  getCollegeListUnauthenticated(search, limit = 10, offset = 0) {
    return this._getUnauthenticated('schools/schools/', {
      limit,
      offset,
      search
    }).then((m) => new ListResponseModel(m, SchoolModel));
  }

  cancelAppointment(id, pending = false) {
    if (pending) {
      return this._patch(
        `counseling_sessions/students/my_appointment_requests/${id}/`,
        {
          canceled: true
        }
      );
    } else {
      return this._post(`counseling_sessions/appointments/${id}/cancel/`);
    }
  }

  getStudentSurveyTemplate() {
    return this._get('surveys/templates/mental-health-survey/').then(
      (user) => new SurveyModel(user)
    );
  }

  getStudentSurveyResponse() {
    return this._get('surveys/responses/mental-health-survey/');
  }

  startNewSurvey(survey_template_id) {
    return this._post('surveys/responses/', {
      survey_template_id
    });
  }

  saveSurveyAnswer(id, question_id, answer_ids, followup_answer) {
    const data = {
      question_id,
      answer_ids
    };

    if (followup_answer) {
      data.followup_answer = followup_answer;
    }

    return this._post(`surveys/responses/${id}/answers/`, data);
  }

  getArticles(filters, limit = 10, offset = 0) {
    const data = filters || {};
    data.limit = limit;
    data.offset = offset;

    return this._get('discussion_forums/topics/', data).then(
      (m) => new ListResponseModel(m, ArticleModel)
    );
  }

  addArticle(data) {
    return this._post('discussion_forums/topics/', data);
  }

  getArticle(id) {
    return this._get(`discussion_forums/topics/${id}/`).then(
      (m) => new ArticleModel(m)
    );
  }

  addCommentToArticle(id, comment) {
    return this._post(`discussion_forums/topics/${id}/comments/`, {
      comment
    });
  }

  getLanguagesList() {
    return this._get('users/languages/');
  }

  getSignedUploadUrl(amount = 1) {
    return this._post('file_uploads/uploads/', {
      // amount,
    }); // .then(response => (response || []).map(m => new UploadUrlModel(m)));
  }

  static async saveFile(url, file) {
    return fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-type': ''
      }
    });
  }
  static async saveFileWithContentType(url, file) {
    return fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-type': file.type
      }
    });
  }

  syncIdentity() {
    return this._post('v2/campus-administration/student/identity-sync/', {});
  }

  getCommunicationPreferences(userId) {
    return this._get('v2/communications/get-preferences', { userId });
  }

  setCommunicationPreferences(userId, preferences) {
    return this._post('v2/communications/set-preferences', { userId, preferences });
  }
}
